// frontend/src/pages/admin/AdminLayout.jsx
import { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Users,
  Settings,
  FileText,
  Image,
  Menu,
  X,
  Zap,        // ✅ اضافه شد
  Box,
  ShoppingCart,
  UserCog,
  Truck,
  MessageSquare,
  Star,
  Calendar,
  CreditCard,
  BarChart3,
  Tag,
  Layers,
  ClipboardList
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import NotificationBell from '../../components/Notifications/NotificationBell';

export const AdminLayout = () => {
  const location = useLocation();
  const { user } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ✅ لیست منوها با آیتم Power Ratings
  const menuItems = [
    // داشبورد
    {
      to: '/admin',
      icon: LayoutDashboard,
      label: 'داشبورد',
      active: location.pathname === '/admin'
    },
    // محصولات
    {
      to: '/admin/products',
      icon: Package,
      label: 'محصولات',
      active: location.pathname.startsWith('/admin/products')
    },
    // پکیج‌ها
    {
      to: '/admin/packages',
      icon: Box,
      label: 'پکیج‌ها',
      active: location.pathname.startsWith('/admin/packages')
    },
    // ✅ توان‌های پکیج (جدید)
    {
      to: '/admin/power-ratings',
      icon: Zap,
      label: 'توان‌های پکیج',
      active: location.pathname === '/admin/power-ratings'
    },
    // سفارشات
    {
      to: '/admin/orders',
      icon: ShoppingBag,
      label: 'سفارشات',
      active: location.pathname.startsWith('/admin/orders')
    },
    // کاربران
    {
      to: '/admin/users',
      icon: Users,
      label: 'کاربران',
      active: location.pathname.startsWith('/admin/users')
    },
    // بلاگ
    {
      to: '/admin/blog',
      icon: FileText,
      label: 'وبلاگ',
      active: location.pathname.startsWith('/admin/blog')
    },
    // بنرها
    {
      to: '/admin/banners',
      icon: Image,
      label: 'بنرها',
      active: location.pathname.startsWith('/admin/banners')
    },
    // تنظیمات
    {
      to: '/admin/settings',
      icon: Settings,
      label: 'تنظیمات',
      active: location.pathname.startsWith('/admin/settings')
    },
  ];

  // بررسی دسترسی ادمین
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">دسترسی غیرمجاز</h1>
          <p className="text-gray-600">شما دسترسی به این بخش ندارید.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar - دسکتاپ */}
      <aside className="hidden md:flex md:flex-col md:w-64 bg-white shadow-lg fixed inset-y-0 z-30">
        <div className="p-4 border-b">
          <h1 className="text-xl font-bold text-blue-600">پنل مدیریت</h1>
        </div>
        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition ${
                    item.active
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <UserCog className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.username}</p>
              <p className="text-xs text-gray-500">مدیر سیستم</p>
            </div>
          </div>
        </div>
      </aside>

      {/* دکمه Hamburger برای موبایل */}
      <button
        onClick={() => setSidebarOpen(true)}
        className="md:hidden fixed top-4 right-4 z-50 p-2 bg-white rounded-lg shadow-lg"
      >
        <Menu className="w-6 h-6 text-gray-700" />
      </button>

      {/* Sidebar - موبایل */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50" onClick={() => setSidebarOpen(false)} />
      )}
      <aside
        className={`md:hidden fixed top-0 right-0 h-full w-72 bg-white shadow-xl z-50 transform transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <h1 className="text-xl font-bold text-blue-600">پنل مدیریت</h1>
          <button onClick={() => setSidebarOpen(false)}>
            <X className="w-6 h-6 text-gray-700" />
          </button>
        </div>
        <nav className="p-4 overflow-y-auto h-[calc(100%-80px)]">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition ${
                    item.active
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* محتوای اصلی */}
      <main className="flex-1 md:mr-64 p-4 md:p-8 pt-16 md:pt-8">
        <Outlet />
      </main>
    </div>
  );
};